import type { SubmitHandler } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { memo, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { AdCreationProgress, CircularProgress, ConfirmationDialog } from 'components/common';
import type { StepInitStatus, StepType } from 'components/layout';
import { FormNavigation, StepperPaper } from 'components/layout';
import { Box } from '@mui/system';
import {
  useEnqueueSnackbar,
  useJobStatusCheck,
  useLocationParams,
  useStepHistorySync,
  useStepper,
} from 'hooks';
import { useGlobalSettings } from 'providers';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import { canNavigateToStep } from 'utils';
import { APP_LIST, type AppCode } from '../../../constants';
import { useCreatePinterestAd } from '../api';
import type { PinterestAdCreateResult } from '../api';
import useCreatePinterestAdParams from '../hooks/useCreatePinterestAdParams';
import type { PinterestFormValues } from '../validation/pinterest.schema';
import { pinterestSchema } from '../validation/pinterest.schema';
import { getPinterestFormDefaultValues } from '../validation/defaultValues';
import PinterestJobResultTable from './PinterestJobResultTable';
import PinterestDeepLinkInitializer from './PinterestDeepLinkInitializer';
import { PINTEREST_STEPS } from './pinterestSteps';
import { PINTEREST_FORM_TITLE, PINTEREST_SERVICE_PATH } from '../constants';

const PinterestForm = memo(() => {
  const { onShowErrorAlert } = useEnqueueSnackbar();
  const { selectedApp } = useGlobalSettings();
  const {
    app: appParam,
    setApp: setAppParam,
    adAccount: adAccountParam,
    setAdAccount: setAdAccountParam,
    flatCreatives: flatCreativesParam,
    creativesFormat: creativesFormatParam,
    setFlatCreatives: setFlatCreativesParam,
    selectedCampaigns: selectedCampaignsParam,
    setSelectedCampaigns: setSelectedCampaignsParam,
    selectedAdSets: selectedAdSetsParam,
    setSelectedAdSets: setSelectedAdSetsParam,
    setFilterCreativesByName: setFilterCreativesByNameParam,
    setFilterCampaignsByName: setFilterCampaignsByNameParam,
    setFilterAdSetsByName: setFilterAdSetsByNameParam,
    step: stepParam,
    setStep: setStepParam,
    reloadLocationParamsToStart,
  } = useLocationParams();

  const {
    watch,
    trigger,
    reset: resetForm,
    handleSubmit,
    setValue,
    getValues,
    formState: { isValid },
  } = useFormContext<PinterestFormValues>();
  const formApp = watch('app');
  const formValues = watch();

  const initialSteps = useMemo(() => PINTEREST_STEPS, []);

  const creativesCompatible = creativesFormatParam !== 'pack';
  const hasUrlCreatives = creativesCompatible && flatCreativesParam.length > 0;

  // Deep links reopen the requested step only when every earlier step has its params.
  const initialStep = useMemo(() => {
    const target = Math.min(stepParam, initialSteps.length - 1);
    if (target <= 0 || !creativesCompatible) return 0;

    if (!hasUrlCreatives) return 0;
    if (target >= 2 && !adAccountParam) return 1;
    if (target >= 3 && !selectedCampaignsParam.length) return 2;
    if (target >= 4 && !selectedAdSetsParam.length) return 3;
    return target;
  }, [
    adAccountParam,
    creativesCompatible,
    hasUrlCreatives,
    initialSteps.length,
    selectedAdSetsParam.length,
    selectedCampaignsParam.length,
    stepParam,
  ]);

  const { currentStep, resetSteps, prevStep, nextStep, goToStep, StepComponent } = useStepper({
    initialSteps,
    defaultStep: initialStep,
  });

  const [isInitializing, setIsInitializing] = useState(initialStep > 0 || !creativesCompatible || hasUrlCreatives);
  const [initStepStatuses, setInitStepStatuses] = useState<StepInitStatus[]>([]);

  const handleInitComplete = useCallback(() => {
    setIsInitializing(false);
  }, []);

  const handleStatusChange = useCallback((statuses: StepInitStatus[]) => {
    setInitStepStatuses(statuses);
  }, []);

  const stepsWithStatus = useMemo<StepType[]>(
    () =>
      isInitializing ? initialSteps.map((step, i) => ({ ...step, initStatus: initStepStatuses[i] })) : initialSteps,
    [isInitializing, initialSteps, initStepStatuses],
  );

  const stepperActiveStep = useMemo(() => {
    if (!isInitializing) return currentStep;
    const loadingIdx = initStepStatuses.findIndex((status) => status === 'loading');
    if (loadingIdx >= 0) return loadingIdx;
    const failedIdx = initStepStatuses.findIndex((status) => status === 'failed');
    if (failedIdx >= 0) return failedIdx;
    return initialStep;
  }, [isInitializing, initStepStatuses, currentStep, initialStep]);

  const clearDeepLinkParams = useCallback(() => {
    setAdAccountParam(undefined);
    setFlatCreativesParam([]);
    setSelectedCampaignsParam([]);
    setSelectedAdSetsParam([]);
    setFilterCreativesByNameParam([]);
    setFilterCampaignsByNameParam([]);
    setFilterAdSetsByNameParam([]);
    setStepParam(0);
  }, [
    setAdAccountParam,
    setFilterAdSetsByNameParam,
    setFilterCampaignsByNameParam,
    setFilterCreativesByNameParam,
    setFlatCreativesParam,
    setSelectedAdSetsParam,
    setSelectedCampaignsParam,
    setStepParam,
  ]);

  const revalidateForStep = useCallback(
    async (targetStep: number) => {
      setValue('currentStep', targetStep);
      await trigger();
    },
    [setValue, trigger],
  );

  const {
    data: jobStatusResponse,
    runJubStatusCheck,
    isLoading: isJobStatusChecking,
    isFinished: isJobStatusFinished,
    clearJobStatus,
  } = useJobStatusCheck<PinterestAdCreateResult>();

  const createPinterestAd = useCreatePinterestAd({
    config: {
      onSuccess: (response) => {
        const jobId = response.job_id as string;
        datadogActions.trackDatadogAction(DATADOG_EVENT.createPinterestAdSuccess, { jobId });
        runJubStatusCheck(jobId);
      },
      onError: (err) => {
        datadogActions.trackDatadogError(err, { source: 'create_pinterest_ad_error' });

        const cause = err?.cause as { details?: string; message?: string } | undefined;
        onShowErrorAlert(cause?.details ?? cause?.message ?? 'Internal Error', { delayAlert: 3500 });
      },
    },
  });

  const { params: createParams, isValid: isPayloadValid } = useCreatePinterestAdParams();

  useEffect(() => {
    setValue('currentStep', currentStep, { shouldValidate: true });
  }, [currentStep, setValue]);

  useStepHistorySync({
    stepParam,
    currentStep,
    stepCount: initialSteps.length,
    goToStep,
    revalidateForStep,
    enabled: !isJobStatusFinished,
  });

  const handleInitReset = useCallback(() => {
    setIsInitializing(false);
    setInitStepStatuses([]);
    goToStep(0);
    resetForm(getPinterestFormDefaultValues(formApp ?? ''));
    clearJobStatus();
  }, [clearJobStatus, formApp, goToStep, resetForm]);

  useEffect(() => {
    if (!formApp) return;
    const nextApp = formApp as AppCode;
    if (appParam === nextApp || !APP_LIST.some(({ code }) => code === nextApp)) return;

    setAppParam(nextApp);
  }, [formApp, appParam, setAppParam]);

  useEffect(() => {
    if (!selectedApp || formApp === String(selectedApp)) return;

    // URL `app` already matches the form: the sidebar selector catching up
    // after a deep link is not a user app switch, so keep URL selections.
    if (appParam && String(appParam) === String(formApp) && String(appParam) !== String(selectedApp)) {
      return;
    }

    resetForm(getPinterestFormDefaultValues(selectedApp));
    resetSteps();
    clearDeepLinkParams();
    clearJobStatus();
  }, [appParam, clearDeepLinkParams, clearJobStatus, formApp, resetForm, resetSteps, selectedApp]);

  useEffect(() => {
    const stepTitle = initialSteps[currentStep]?.title;
    if (!stepTitle) return;

    datadogActions.trackDatadogAction(DATADOG_EVENT.pinterestFormStepChange, { stepTitle, stepIndex: currentStep });
  }, [currentStep, initialSteps]);

  const onSubmit = useCallback<SubmitHandler<PinterestFormValues>>(() => {
    datadogActions.trackDatadogAction(DATADOG_EVENT.pinterestFormSubmitAttempt, {
      app: createParams.app,
      creativesCount: createParams.creatives.length,
      adGroupsCount: createParams.ad_groups.length,
    });
    createPinterestAd.mutate(createParams);
  }, [createParams, createPinterestAd]);

  const isLastStep = currentStep === initialSteps.length - 1;

  const handleNextStep = useCallback(async () => {
    const valid = await trigger();
    if (!valid) return;

    if (isLastStep) {
      void handleSubmit(onSubmit)();
      return;
    }

    nextStep();
    setStepParam(currentStep + 1, 'pushIn');
    void revalidateForStep(currentStep + 1);
  }, [trigger, isLastStep, handleSubmit, onSubmit, nextStep, setStepParam, currentStep, revalidateForStep]);

  const handlePrevStep = useCallback(() => {
    const target = Math.max(currentStep - 1, 0);
    prevStep();
    setStepParam(target, 'pushIn');
    void revalidateForStep(target);
  }, [prevStep, revalidateForStep, setStepParam, currentStep]);

  const handleRestart = useCallback(() => {
    datadogActions.trackDatadogAction(DATADOG_EVENT.pinterestFormRestart);
    reloadLocationParamsToStart();
  }, [reloadLocationParamsToStart]);

  const handleLaunchMoreAds = useCallback(() => {
    window.open(PINTEREST_SERVICE_PATH, '_blank', 'noopener');
  }, []);

  const isNavigationLocked = createPinterestAd.isPending || !!isJobStatusChecking || isInitializing;
  const isDisabledNext = !isValid || isNavigationLocked || isJobStatusFinished || (isLastStep && !isPayloadValid);

  const handleStepClick = useCallback(
    (index: number) => {
      if (isNavigationLocked || isJobStatusFinished || index === currentStep) return;
      if (!canNavigateToStep(index, currentStep, getValues(), pinterestSchema)) return;

      goToStep(index);
      setStepParam(index, 'pushIn');
      void revalidateForStep(index);
    },
    [currentStep, getValues, goToStep, isJobStatusFinished, isNavigationLocked, revalidateForStep, setStepParam],
  );

  const stepperSteps = useMemo<StepType[]>(() => {
    if (isNavigationLocked || isJobStatusFinished) {
      return stepsWithStatus.map((step) => ({ ...step, clickable: false }));
    }

    return stepsWithStatus.map((step, index) => ({
      ...step,
      clickable: canNavigateToStep(index, currentStep, formValues, pinterestSchema),
    }));
  }, [currentStep, formValues, isJobStatusFinished, isNavigationLocked, stepsWithStatus]);

  return (
    <StepperPaper
      title={PINTEREST_FORM_TITLE}
      steps={stepperSteps}
      activeStep={stepperActiveStep}
      onStepClick={handleStepClick}
    >
      <>
        {isJobStatusChecking ? (
          <AdCreationProgress onLaunchMoreAds={handleLaunchMoreAds} />
        ) : (
          <Suspense fallback={<CircularProgress />}>
            {isJobStatusFinished && jobStatusResponse ? (
              <PinterestJobResultTable rows={jobStatusResponse.result} />
            ) : isInitializing ? (
              <Box sx={{ display: 'flex', width: '100%', height: '100%', flex: 1 }}>
                <PinterestDeepLinkInitializer
                  targetStep={initialStep}
                  onComplete={handleInitComplete}
                  onReset={handleInitReset}
                  onStatusChange={handleStatusChange}
                />
              </Box>
            ) : (
              <StepComponent />
            )}
          </Suspense>
        )}
        <FormNavigation
          isJobStatusFinished={isJobStatusFinished}
          onRestart={handleRestart}
          isDisabledRestart={!currentStep && !isJobStatusFinished}
          onBack={handlePrevStep}
          isDisabledBack={!currentStep || isNavigationLocked || isJobStatusFinished}
          onNext={() => void handleNextStep()}
          isDisabledNext={isDisabledNext}
          isLoadingNext={createPinterestAd.isPending}
          titleNext={isLastStep ? 'Create Ads' : 'Next'}
        />
      </>
      {!jobStatusResponse?.result && (
        <ConfirmationDialog isUnblock={createPinterestAd.isSuccess && isJobStatusFinished} />
      )}
    </StepperPaper>
  );
});

PinterestForm.displayName = 'PinterestForm';

export default PinterestForm;
